import React from "react";
import { Link } from "react-router-dom";
import { Globe, ArrowRight, Lock, ShieldCheck, Plug } from "lucide-react";

import { usePageMetadata } from "../hooks/usePageMetadata";
import "./IntegrationsDirectoryPage.css";

const INTEGRATIONS = [
  {
    id: "wordpress",
    name: "WordPress",
    description:
      "Embed your Brandmykitchen asset on WordPress with secure API keys and shortcodes.",
    statusLabel: "Available now",
    highlights: ["Secure shortcodes", "WordPress.com compatible", "Menu caching & API monitoring"],
    to: "/integrations/wordpress",
    icon: Globe,
  },
  {
    id: "webflow",
    name: "Webflow & no‑code",
    description: "Reusable embed codes for Webflow, Framer, Squarespace and other site builders.",
    statusLabel: "Coming soon",
    highlights: ["Universal embed", "Dynamic data feeds"],
    to: null,
    icon: Plug,
  },
  {
    id: "headless",
    name: "Custom / headless",
    description: "Direct API access for native apps or bespoke frontends with per-key limits.",
    statusLabel: "Coming soon",
    highlights: ["REST API", "Per-key throttling"],
    to: null,
    icon: ShieldCheck,
  },
];

export default function IntegrationsDirectoryPage() {
  usePageMetadata({
    title: "Integrations · Brandmykitchen",
    description: "Pick an integration to securely embed your Brandmykitchen menu.",
  });

  return (
    <div className="integrations-directory">
      <header className="integrations-directory__hero">
        <div>
          <p className="integrations-directory__eyebrow">Integrations</p>
          <h1>Connect Brandmykitchen to your favourite platforms</h1>
          <p>
            Choose how you embed your live store. Each integration comes with tailored docs, secure API
            keys, and platform-specific guidance.
          </p>
        </div>
        <div className="integrations-directory__status">
          <Lock size={18} />
          <span>Secure requests are enforced per plan.</span>
        </div>
      </header>

      <section className="integrations-directory__grid">
        {INTEGRATIONS.map((integration) => (
          <IntegrationCard key={integration.id} integration={integration} />
        ))}
      </section>
    </div>
  );
}

function IntegrationCard({ integration }) {
  const Icon = integration.icon ?? Globe;
  const TagIcon = integration.to ? ArrowRight : Lock;

  const cardBody = (
    <>
      <div className="integration-card__header">
        <div className="integration-card__badge">
          <Icon size={22} />
        </div>
        <div>
          <p className="integration-card__status">{integration.statusLabel}</p>
          <h2>{integration.name}</h2>
        </div>
      </div>
      <p className="integration-card__description">{integration.description}</p>
      {integration.highlights && (
        <ul className="integration-card__highlights">
          {integration.highlights.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
      <div className="integration-card__cta">
        <span>{integration.to ? "Configure" : "In preview"}</span>
        <TagIcon size={18} />
      </div>
    </>
  );

  if (!integration.to) {
    return (
      <article className="integration-card integration-card--soon">
        {cardBody}
      </article>
    );
  }

  return (
    <Link to={integration.to} className="integration-card integration-card--available">
      {cardBody}
    </Link>
  );
}
